/**
 * officeAddress — s110 (Dr. Rami 2026-10-16; mockup "Orella Directory Office Addresses" MjZ3WSmjpA5PNrJBixwixK,
 * copy claude/ORELLA_DIRECTORY_ADDRESS_COPY_v1.md). The rules every directory-office surface (the share list,
 * the map card, the office picker, the send screen, the Doctors tab) reads, so they cannot drift. PURE.
 *
 * A directory row from GET /providers/search (or /providers/<npi>/offices) carries:
 *   addressConfirmed  false when the NPPES address looks like a home; the API then sends city, state and ZIP
 *                     only (address.line1 / line2 null), and the row is drawn as an AREA, never a street pin
 *   officeCount       how many offices the clinician has (1 when unknown)
 *   practice          the office's group name, when the Medicare file names one
 * A row from an older API has none of these: it is one confirmed office, as before.
 */
export function officeConfirmed(row) {
  return !(row && row.addressConfirmed === false)
}

export function otherOffices(row) {
  const n = Number(row && row.officeCount)
  return Number.isFinite(n) && n > 1 ? n : 0
}

/** "1.8 mi", or "~4 mi" for an address we only know the area of (whole miles: it is a ZIP centre). */
export function officeMilesText(row, t) {
  const m = row && typeof row.miles === 'number' && Number.isFinite(row.miles) ? row.miles : null
  if (m === null) return ''
  if (!officeConfirmed(row)) {
    return t('findDoctor.milesApprox', { defaultValue: '~{{n}} mi', n: Math.max(1, Math.round(m)) })
  }
  return t('findDoctor.miles', { defaultValue: '{{n}} mi', n: Math.round(m * 10) / 10 })
}

/** The street lines of an office, or '' when the address is not confirmed (never a home street). */
export function officeStreet(row) {
  if (!officeConfirmed(row)) return ''
  const a = (row && row.address) || {}
  return [a.line1, a.line2].filter(Boolean).join(', ')
}

/** "City, ST 90210". */
export function officeCityLine(row) {
  const a = (row && row.address) || {}
  const cs = [a.city, a.state].filter(Boolean).join(', ')
  return [cs, a.zip].filter(Boolean).join(' ')
}

/** A maps link for directions, or null when we do not know the street (an area cannot be driven to). */
export function officeDirectionsUrl(row) {
  const street = officeStreet(row)
  if (!street) return null
  const a = (row && row.address) || {}
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([a.line1, a.city, a.state, a.zip].filter(Boolean).join(', '))}`
}

/** The office the picker selects first: the nearest one with a fax on file, else the nearest (Dr. Rami's
 *  default, 2026-10-16). `offices` are in the API's nearest-first order. */
export function defaultOfficeIndex(offices) {
  if (!Array.isArray(offices) || !offices.length) return -1
  const withFax = offices.findIndex((o) => o && o.faxOnFile)
  return withFax >= 0 ? withFax : 0
}

/** The provider handed to the send screen once an office is chosen: the clinician's row with THAT office's
 *  address, phone, fax and distance (and its own confirmed flag). */
export function withOffice(provider, office) {
  if (!provider || !office) return provider
  return {
    ...provider,
    address: office.address, phone: office.phone, fax: office.fax, faxOnFile: office.faxOnFile,
    faxSource: office.faxSource, miles: office.miles, lat: office.lat, lng: office.lng, placed: office.placed,
    addressConfirmed: office.addressConfirmed, practice: office.practice || null, officeSource: office.officeSource,
    officeChosen: true,
  }
}

/**
 * s126 (TestFlight build 92 note 2: a Los Angeles doctor's sheet listed San Jose at 300 mi and Boston at 2,590 mi beside
 * the office 0.1 mi away). Offices farther than FAR_OFFICE_MILES go into a closed "Farther away" group under the near ones.
 * When every office is far, or no office has a distance, nothing is grouped. Order inside each group is kept.
 */
export const FAR_OFFICE_MILES = 50
export function splitFarOffices(offices, limit = FAR_OFFICE_MILES) {
  const list = Array.isArray(offices) ? offices : []
  const isFar = (o) => o && typeof o.miles === 'number' && Number.isFinite(o.miles) && o.miles > limit
  const near = list.filter((o) => !isFar(o))
  const far = list.filter(isFar)
  return near.length ? { near, far } : { near: list, far: [] }
}
/** The office selected first: the nearest NEAR office with a fax on file, else the first near office (a far office is
 *  never pre-selected while it sits in the closed group). Returns an index into the FULL list. */
export function defaultNearOfficeIndex(offices, limit = FAR_OFFICE_MILES) {
  const list = Array.isArray(offices) ? offices : []
  const { near } = splitFarOffices(list, limit)
  const i = defaultOfficeIndex(near)
  return i < 0 ? -1 : list.indexOf(near[i])
}
